import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ManagePdfReportsDataService } from 'src/app/reports/data-access/manage-pdf-reports-data.service';
import { ManagePdfReportSummary } from 'src/app/reports/data-access/manage-pdf-reports.types';

export interface SingleProjectPdfReportDialogData {
    projectId: number;
    projectName: string;
}

export interface NoteTypeOption {
    id: number;
    name: string;
    label: string;
    selected: boolean;
}

@Component({
    selector: 'eic-single-project-pdf-report-dialog',
    templateUrl: './single-project-pdf-report-dialog.component.html',
    styleUrl: './single-project-pdf-report-dialog.component.scss'
})
export class SingleProjectPdfReportDialogComponent implements OnInit {
    newReport = true;

    title: string;
    description = '';
    fileName: string;
    fileNameModifiedByUser = false;
    runImmediately = true;

    // Content: Project Notes duration
    // -1 = All, 0 = None, 1 = within last X months
    projectNotesDuration = -1;
    createdWithinMonths: number | null = null;

    // Content: Note Types
    noteTypes: NoteTypeOption[] = [
        { id: 1, name: 'contractActivity', label: 'Contract Activity', selected: true },
        { id: 2, name: 'developmentNews', label: 'Development', selected: true },
        { id: 3, name: 'planningProcess', label: 'Planning', selected: true },
        { id: 4, name: 'financialNews', label: 'Financial', selected: true },
        { id: 5, name: 'generalNews', label: 'General', selected: true },
        { id: 6, name: 'memberNotes', label: 'Member Notes', selected: true }
    ];

    // Add to existing report options
    reports: ManagePdfReportSummary[] = [];
    selectedReportGuid: string | null = null;
    selectedReport: ManagePdfReportSummary | null = null;
    isLoadingReports = false;

    isRequesting = false;
    requestSent = false;
    success = false;
    error: string | null = null;

    constructor(
        private dialogRef: MatDialogRef<SingleProjectPdfReportDialogComponent>,
        private managePdfReportsDataService: ManagePdfReportsDataService,
        @Inject(MAT_DIALOG_DATA) public data: SingleProjectPdfReportDialogData
    ) {
        const rawName = (data?.projectName || '').trim();
        this.title = rawName.substring(0, 49);
        this.description = rawName;
        this.fileName = this.sanitizeFileName(this.title);
    }

    ngOnInit(): void {
        // Initial setup complete in constructor
    }

    get submitButtonCaption(): string {
        if (this.newReport) {
            return this.runImmediately ? 'Run Report Now' : 'Create Report Definition';
        }
        return 'Update Report Definition';
    }

    get isSubmitDisabled(): boolean {
        if (this.isRequesting) {
            return true;
        }

        if (this.newReport) {
            if (!this.title || !this.title.trim()) {
                return true;
            }
            if (!this.fileName || !this.fileName.trim()) {
                return true;
            }
            if (this.projectNotesDuration === 1) {
                if (!this.createdWithinMonths || this.createdWithinMonths < 1) {
                    return true;
                }
            }
            return false;
        }

        // Add to existing report mode
        return !this.selectedReportGuid;
    }

    onModeChange(isNew: boolean): void {
        this.newReport = isNew;
        this.error = null;

        if (!this.newReport && this.reports.length === 0) {
            this.loadReports();
        }
    }

    onTitleChange(value: string): void {
        this.title = value;
        if (!this.fileNameModifiedByUser) {
            this.fileName = this.sanitizeFileName(value ? value.substring(0, 54) : '');
        }
    }

    onFileNameChange(value: string): void {
        this.fileNameModifiedByUser = true;
        this.fileName = value;
    }

    onReportSelected(reportGuid: string): void {
        this.selectedReportGuid = reportGuid;
        const found = this.reports.find(
            (r) => (r.guid || r.reportGuid || r.reportId || r.reportID) === reportGuid
        );
        this.selectedReport = found || null;
        if (found) {
            this.description = (found.description as string) || '';
            this.fileName = (found.fileName as string) || '';
        }
    }

    loadReports(): void {
        this.isLoadingReports = true;
        this.managePdfReportsDataService.getReports().subscribe({
            next: (reports) => {
                this.reports = reports || [];
                this.isLoadingReports = false;
            },
            error: () => {
                this.reports = [];
                this.isLoadingReports = false;
            }
        });
    }

    cancel(): void {
        this.dialogRef.close();
    }

    close(): void {
        this.dialogRef.close();
    }

    submit(): void {
        if (this.isSubmitDisabled) {
            return;
        }

        if (this.newReport) {
            this.createReport();
        } else {
            this.addProjectToExistingReport();
        }
    }

    createReport(): void {
        if (this.isRequesting || !this.title || !this.title.trim()) {
            return;
        }

        this.isRequesting = true;
        this.error = null;

        const selectedNoteTypeIds = this.noteTypes
            .filter((nt) => nt.selected)
            .map((nt) => nt.id);

        const noteAgeMonths = this.projectNotesDuration === 1
            ? (this.createdWithinMonths || 1)
            : this.projectNotesDuration;

        const formattedFileName = this.ensurePdfExtension(this.fileName || this.title);

        this.managePdfReportsDataService
            .createReport({
                title: this.title.trim(),
                description: this.description ? this.description.trim() : '',
                fileName: formattedFileName,
                highlightDays: -30,
                sort: {
                    order: 'ProjectName',
                    direction: 'ASC',
                    pageBreak: false
                },
                sortOrder: 'ProjectName',
                sortDesc: false,
                criteria: '',
                projects: [this.data.projectId],
                projectIds: [this.data.projectId],
                noteAgeMonths,
                noteTypes: selectedNoteTypeIds,
                trackingStatuses: [],
                runImmediately: this.runImmediately,
                newReport: true
            })
            .subscribe({
                next: (res: any) => {
                    if (this.runImmediately) {
                        const reportGuid = res?.guid || res?.reportGuid || res?.reportId || res?.reportID ||
                            res?.data?.guid || res?.data?.reportGuid || res?.data?.reportId || res?.data?.reportID ||
                            (typeof res === 'string' || typeof res === 'number' ? res : null);

                        if (reportGuid) {
                            this.managePdfReportsDataService.runReport(String(reportGuid)).subscribe({
                                next: () => {
                                    this.isRequesting = false;
                                    this.requestSent = true;
                                    this.success = true;
                                },
                                error: (runErr) => {
                                    this.isRequesting = false;
                                    this.requestSent = true;
                                    this.success = false;
                                    this.error = runErr?.error?.message || runErr?.statusText || 'The report could not be initiated.';
                                }
                            });
                            return;
                        }
                    }
                    this.isRequesting = false;
                    this.requestSent = true;
                    this.success = true;
                },
                error: (err) => {
                    this.isRequesting = false;
                    this.requestSent = true;
                    this.success = false;
                    this.error = err?.error?.message || err?.statusText || 'The report request failed.';
                }
            });
    }

    addProjectToExistingReport(): void {
        if (!this.selectedReportGuid) {
            return;
        }

        this.isRequesting = true;
        this.error = null;

        this.managePdfReportsDataService
            .addProjects(this.selectedReportGuid, [this.data.projectId])
            .subscribe({
                next: () => {
                    this.isRequesting = false;
                    this.requestSent = true;
                    this.success = true;
                },
                error: (err) => {
                    this.isRequesting = false;
                    this.requestSent = true;
                    this.success = false;
                    this.error = err?.error?.message || err?.statusText || 'Failed to update report definition.';
                }
            });
    }

    getReportKey(report: ManagePdfReportSummary): string {
        return String(report.guid || report.reportGuid || report.reportId || report.reportID || '');
    }

    getReportTitle(report: ManagePdfReportSummary): string {
        return String(report.title || report.name || report.reportTitle || report.reportName || 'Untitled Report');
    }

    private sanitizeFileName(value: string): string {
        return (value || '')
            .replace(/[\\/:*?"<>|]+/g, '_')
            .replace(/\s+/g, '-');
    }

    private ensurePdfExtension(fileName: string): string {
        const trimmed = (fileName || '').trim();
        return trimmed.toLowerCase().endsWith('.pdf') ? trimmed : `${trimmed}.pdf`;
    }
}
